import { FormData } from '../types';

/**
 * Checks if form data has been modified from its initial state
 * @param currentData - Current form data
 * @param initialData - Initial form data
 * @returns true if form has been modified, false otherwise
 */
export const hasFormDataChanged = (currentData: FormData, initialData: FormData): boolean => {
  // Check if any text fields have been modified (excluding photos and signatures)
  for (const [key, value] of Object.entries(currentData)) {
    if (key === 'photos' || key === 'signatures') continue;
    
    const initialValue = initialData[key];
    const currentValue = value;
    
    // Skip if both values are empty or undefined
    if ((!currentValue || currentValue.toString().trim() === '') && 
        (!initialValue || initialValue.toString().trim() === '')) {
      continue;
    }
    
    // Only consider it changed if the current value is meaningful and different from initial
    if (currentValue && currentValue.toString().trim() !== '' && currentValue !== initialValue) {
      return true;
    }
  }

  // Check if any photos have changed (added or removed)
  if (currentData.photos) {
    for (const [key, files] of Object.entries(currentData.photos)) {
      const initialFiles = initialData.photos?.[key] || [];
      if (files && files.length !== initialFiles.length) {
        return true;
      }
    }
  }

  // Check if any signatures have been added
  if (currentData.signatures) {
    for (const [key, signature] of Object.entries(currentData.signatures)) {
      const initialSignature = initialData.signatures?.[key];
      if (signature && signature.trim() !== '' && signature !== initialSignature) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Strict change detection for persistence decisions.
 * Unlike hasFormDataChanged, this treats removals (e.g. signature -> null)
 * as real changes so draft saves are not skipped.
 */
export const hasFormDataChangedForPersistence = (
  currentData: FormData,
  previousData: FormData,
): boolean => {
  // Compare all non-media dynamic fields strictly.
  const fieldKeys = new Set([
    ...Object.keys(currentData),
    ...Object.keys(previousData),
  ]);
  for (const key of fieldKeys) {
    if (key === 'photos' || key === 'signatures') continue;

    const currentValue = currentData[key];
    const previousValue = previousData[key];
    if (currentValue !== previousValue) {
      return true;
    }
  }

  // Compare photos strictly so additions/removals are persisted.
  const currentPhotos = currentData.photos || {};
  const previousPhotos = previousData.photos || {};
  const photoKeys = new Set([
    ...Object.keys(currentPhotos),
    ...Object.keys(previousPhotos),
  ]);

  for (const key of photoKeys) {
    const currFiles = currentPhotos[key] || [];
    const prevFiles = previousPhotos[key] || [];
    if (currFiles.length !== prevFiles.length) return true;
    for (let i = 0; i < currFiles.length; i++) {
      if (currFiles[i] !== prevFiles[i]) return true;
    }
  }

  // Compare signatures strictly so clearing is persisted.
  const currentSignatures = currentData.signatures || {};
  const previousSignatures = previousData.signatures || {};
  const signatureKeys = new Set([
    ...Object.keys(currentSignatures),
    ...Object.keys(previousSignatures),
  ]);

  for (const key of signatureKeys) {
    const currentSignature = currentSignatures[key] ?? null;
    const previousSignature = previousSignatures[key] ?? null;
    if (currentSignature !== previousSignature) {
      return true;
    }
  }

  return false;
};

/**
 * Creates initial form data for comparison
 * @param formData - Current form data
 * @returns A copy of the form data that can be used as initial state
 */
export const createInitialFormData = (formData: FormData): FormData => {
  return {
    ...formData,
    photos: { ...formData.photos },
    signatures: { ...formData.signatures }
  };
};
